import { createAsyncThunk } from "@reduxjs/toolkit";
import { db, storage } from "@/lib/firebase";
import {
  collection,
  addDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  serverTimestamp,
  getDocs,
  updateDoc,
  getDoc,
  doc,
  increment,
  Timestamp
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { toast } from "@/components/ui/use-toast";
import {
  setSellerMessagesCount,
  setConversationLoading,
  setConversations,
  setMessages,
  setMessagesLoading,
  setTotalUnreadCount
} from "./partChatSlice";

const uploadFileToStorage = async (file, senderId) => {
  try {
    const timestamp = Date.now();
    const fileName = `${senderId}_${timestamp}_${file.name}`;
    const storageRef = ref(storage, `chatMedia/${fileName}`);

    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

export const sendMessage = createAsyncThunk(
  "chat/sendMessage",
  async (
    { receiverId, senderId, partId, content = "", files = [], partName = "" },
    { rejectWithValue }
  ) => {
    try {
      const chatsRef = collection(db, "chats");
      const q = query(
        chatsRef,
        where("participants", "array-contains", senderId),
        where("partId", "==", partId)
      );
      const querySnapshot = await getDocs(q);

      let chatDoc = null;
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.participants.includes(receiverId)) {
          chatDoc = { id: docSnap.id, ...data };
        }
      });

      const hasFiles = files && files.length > 0;
      const hasText = content && content.trim();

      let fileType = "text";
      let lastMessage = content || "sent a message";

      if (hasFiles) {
        const imageFiles = files.filter((file) =>
          file.type.startsWith("image/")
        );
        const videoFiles = files.filter((file) =>
          file.type.startsWith("video/")
        );

        if (imageFiles.length > 0 && videoFiles.length > 0) {
          fileType = "mixed";
          lastMessage = hasText ? `${content}` : "media";
        } else if (imageFiles.length > 0) {
          fileType = "image";
          lastMessage = hasText ? `${content}` : `image`;
        } else if (videoFiles.length > 0) {
          fileType = "video";
          lastMessage = hasText ? `${content}` : `video`;
        }
      }

      let chatId;
      if (chatDoc) {
        chatId = chatDoc.id;
        await updateDoc(doc(db, "chats", chatId), {
          updatedAt: serverTimestamp(),
          lastMessage: lastMessage,
          [`count.${receiverId}`]: increment(1)
        });
      } else {
        const newChat = {
          participants: [senderId, receiverId],
          partId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isRead: [],
          count: { [senderId]: 0, [receiverId]: 1 },
          lastMessage: lastMessage,
          partName: partName
        };
        const chatRef = await addDoc(chatsRef, newChat);
        chatId = chatRef.id;
      }

      let mediaUrls = [];
      if (hasFiles) {
        try {
          const uploadPromises = files.map((file) =>
            uploadFileToStorage(file, senderId)
          );
          mediaUrls = await Promise.all(uploadPromises);
        } catch (uploadError) {
          console.error("Error uploading files:", uploadError);
          toast({
            title: "Upload Failed",
            description: "Failed to upload media files. Please try again.",
            variant: "destructive"
          });
          return rejectWithValue("File upload failed");
        }
      }

      const messageData = {
        content: content || "",
        createdAt: serverTimestamp(),
        senderId,
        receiverId,
        fileType
      };

      if (mediaUrls.length > 0) {
        messageData.media = mediaUrls;
        messageData.fileTypes = files.map((file) => {
          if (file.type.startsWith("image/")) return "image";
          if (file.type.startsWith("video/")) return "video";
          return "file";
        });
      }

      const messagesRef = collection(db, "chats", chatId, "messages");
      await addDoc(messagesRef, messageData);

      return { success: true, chatId };
    } catch (error) {
      console.error("Error sending message to Firestore:", error);
      toast({
        title: "Message Not Sent",
        description: error.message,
        variant: "destructive"
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchConversations =
  ({ userId }) =>
  (dispatch) => {
    try {
      dispatch(setConversationLoading(true));

      const chatsRef = collection(db, "chats");
      const q = query(
        chatsRef,
        where("participants", "array-contains", userId),
        orderBy("updatedAt", "desc")
      );

      const unsubscribe = onSnapshot(q, async (querySnapshot) => {
        const chats = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        if (!chats.length) {
          dispatch(setConversations([]));
          dispatch(setConversationLoading(false));
          return;
        }

        // --- Cache for user and part fetches ---
        const userCache = {};
        const partCache = {};

        // --- Fetch all related users and parts in parallel ---
        const enhancedChats = await Promise.all(
          chats.map(async (chatData) => {
            const otherUserId = chatData.participants.find(
              (id) => id !== userId
            );

            let otherUserDetail = null;
            let partInfo = null;

            if (otherUserId) {
              if (userCache[otherUserId]) {
                otherUserDetail = userCache[otherUserId];
              } else {
                try {
                  const userSnap = await getDoc(doc(db, "users", otherUserId));
                  if (userSnap.exists()) {
                    otherUserDetail = { id: userSnap.id, ...userSnap.data() };
                    userCache[otherUserId] = otherUserDetail;
                  }
                } catch (e) {
                  console.error("User fetch error:", e);
                }
              }
            }

            if (chatData.partId) {
              if (partCache[chatData.partId]) {
                partInfo = partCache[chatData.partId];
              } else {
                try {
                  const partSnap = await getDoc(
                    doc(db, "parts", chatData.partId)
                  );
                  if (partSnap.exists()) {
                    partInfo = { id: partSnap.id, ...partSnap.data() };
                    partCache[chatData.partId] = partInfo;
                  }
                } catch (e) {
                  console.error("Part fetch error:", e);
                }
              }
            }

            return { ...chatData, partInfo, otherUserDetail };
          })
        );

        dispatch(setConversations(enhancedChats));
        dispatch(setConversationLoading(false));
      });

      return unsubscribe;
    } catch (error) {
      dispatch(setConversationLoading(false));
      console.error("Error subscribing to conversations:", error);
    }
  };

export const fetchMessages =
  ({ sellerId, buyerId, partId }) =>
  async (dispatch) => {
    try {
      dispatch(setMessagesLoading(true));

      const q = query(
        collection(db, "chats"),
        where("partId", "==", partId),
        where("participants", "array-contains", sellerId)
      );

      const querySnapshot = await getDocs(q);

      let chatDoc = null;
      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.participants.includes(buyerId)) {
          chatDoc = { id: docSnap.id, ...data };
        }
      });

      if (!chatDoc) {
        dispatch(setMessages([]));
        dispatch(setMessagesLoading(false));
        return;
      }

      const messagesRef = collection(db, "chats", chatDoc.id, "messages");
      const messagesQuery = query(messagesRef, orderBy("createdAt", "asc"));

      return onSnapshot(messagesQuery, (snapshot) => {
        const messages = snapshot.docs.map((msg) => ({
          id: msg.id,
          ...msg.data()
        }));
        dispatch(setMessages(messages));
        dispatch(setMessagesLoading(false));
      });
    } catch (error) {
      dispatch(setMessagesLoading(false));
      console.error("Error fetching messages:", error);
    }
  };

export const markMessagesAsRead = createAsyncThunk(
  "chat/markMessagesAsRead",
  async ({ sellerId, buyerId, partId, currentUserId }, { rejectWithValue }) => {
    try {
      const chatsRef = collection(db, "chats");
      const q = query(
        chatsRef,
        where("participants", "array-contains", sellerId),
        where("partId", "==", partId)
      );

      const querySnapshot = await getDocs(q);
      let chatDoc = null;

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.participants.includes(buyerId)) {
          chatDoc = { id: docSnap.id, ...data };
        }
      });

      if (chatDoc) {
        const currentUser = currentUserId || buyerId;
        const chatRef = doc(db, "chats", chatDoc.id);
        if (chatDoc.count && chatDoc.count[currentUser] > 0) {
          await updateDoc(chatRef, {
            [`count.${currentUser}`]: 0
          });
        }
      }

      return { success: true };
    } catch (error) {
      console.error("Error marking messages as read:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateIsRead = createAsyncThunk(
  "chats/updateIsRead",
  async (
    { currentUserId, receiverId, partId },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const chatsRef = collection(db, "chats");

      const q = query(
        chatsRef,
        where("participants", "array-contains", currentUserId),
        where("partId", "==", partId)
      );
      const querySnapshot = await getDocs(q);

      let chatDoc = null;

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.participants.includes(receiverId)) {
          chatDoc = { id: docSnap.id, ...data };
        }
      });

      if (!chatDoc) {
        return rejectWithValue("No chat found with both participants.");
      }

      const chatRef = doc(db, "chats", chatDoc.id);
      const chatSnapshot = await getDoc(chatRef);

      if (chatSnapshot.exists()) {
        const chatData = chatSnapshot.data();
        let isRead = chatData.isRead || [];

        const existingIndex = isRead.findIndex(
          (r) => r.readBy === currentUserId
        );

        if (existingIndex !== -1) {
          isRead[existingIndex].readAt = Timestamp.now();
        } else {
          isRead.push({ readBy: currentUserId, readAt: Timestamp.now() });
        }
        await updateDoc(chatRef, {
          isRead,
          [`count.${currentUserId}`]: 0
        });
      }

      return { success: true, chatId: chatDoc.id };
    } catch (error) {
      console.error("Error updating isRead:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSellerById = createAsyncThunk(
  "partChat/fetchSellerById",
  async ({ userId }, { rejectWithValue }) => {
    try {
      if (!userId) throw new Error("userId is required");

      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        throw new Error("Seller not found");
      }

      return { id: userSnap.id, ...userSnap.data() };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const SellerMessagesCount = createAsyncThunk(
  "messages/sellerMessagesCount",
  async (userId, { dispatch }) => {
    try {
      const subsRef = collection(db, "subscriptions");
      const subsSnapshot = await getDocs(subsRef);

      let planId = null;
      let currentPeriodStart = null;
      let currentPeriodEnd = null;
      let status = null;

      subsSnapshot.forEach((subDoc) => {
        const data = subDoc.data();
        if (data?.userId === userId) {
          planId = data.planId || null;
          status = data.status || null;
          currentPeriodStart = data.currentPeriodStart || null;
          currentPeriodEnd = data.currentPeriodEnd || null;
        }
      });

      if (!planId || !currentPeriodStart) {
        console.warn("No subscription found for user:", userId);
        dispatch(
          setSellerMessagesCount({
            count: 0,
            planId: null,
            currentPeriodStart: null,
            currentPeriodEnd: null,
            status: null
          })
        );
        return;
      }

      const chatsRef = collection(db, "chats");
      const q = query(
        chatsRef,
        where("participants", "array-contains", userId)
      );

      const chatCounts = {};

      const unsubscribeChats = onSnapshot(q, (querySnapshot) => {
        querySnapshot.forEach((chatDoc) => {
          const chatId = chatDoc.id;
          const messagesRef = collection(db, "chats", chatId, "messages");

          if (chatCounts[chatId]?.unsubscribe) {
            chatCounts[chatId].unsubscribe();
          }

          const unsubscribeMessages = onSnapshot(
            messagesRef,
            (messagesSnapshot) => {
              let chatCount = 0;
              const periodStartMillis = new Date(currentPeriodStart).getTime();

              messagesSnapshot.forEach((msgDoc) => {
                const data = msgDoc.data();
                if (
                  data.senderId === userId &&
                  data.createdAt?.toMillis() >= periodStartMillis
                ) {
                  chatCount++;
                }
              });

              chatCounts[chatId] = {
                count: chatCount,
                unsubscribe: unsubscribeMessages
              };

              const totalCount = Object.values(chatCounts).reduce(
                (acc, c) => acc + (c.count || 0),
                0
              );

              dispatch(
                setSellerMessagesCount({
                  count: totalCount,
                  planId,
                  status,
                  currentPeriodStart,
                  currentPeriodEnd
                })
              );
            }
          );
        });
      });
      return unsubscribeChats;
    } catch (error) {
      console.error("Error fetching seller messages count:", error);
      throw error;
    }
  }
);

export const listenToBuyerCount = createAsyncThunk(
  "chats/listenToBuyerCount",
  async ({ userId }, { dispatch, rejectWithValue }) => {
    try {
      const chatsRef = collection(db, "chats");
      const q = query(
        chatsRef,
        where("participants", "array-contains", userId)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        let totalUnread = 0;

        snapshot.forEach((doc) => {
          const chatData = doc.data();

          if (chatData?.count && typeof chatData.count === "object") {
            Object.entries(chatData.count).forEach(([id, cnt]) => {
              if (id === userId) {
                const unread = typeof cnt === "number" ? cnt : Number(cnt || 0);
                totalUnread += unread;
              }
            });
          }
        });

        dispatch(setTotalUnreadCount(totalUnread));
      });

      return unsubscribe;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
